import {
  CanAccess,
  ResourceContextProvider,
  useCanAccess,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import type { ReactNode } from "react";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable, DataTableEmpty } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";

import { formatMxn } from "../quoter/PlanSchedule";
import type { InventoryItem, PropertyOptionStatus } from "../types";
import { InventoryFilters, usePropertyTypes } from "./InventoryFilters";
import { InventoryStatsCard } from "./InventoryStatsCard";
import {
  KeySpecs,
  OperationLabel,
  OpcionStatusPill,
  OriginPill,
  ResponsableLabel,
  ResponsableSalesProvider,
} from "./OpcionBadges";
import { INVENTORY_DEFAULT_FILTERS } from "./opcionRules";

/** Batches the responsable lookup for the rows of the current page. */
const ResponsableBatch = ({ children }: { children: ReactNode }) => {
  const { data } = useListContext<InventoryItem>();
  return (
    <ResponsableSalesProvider options={data ?? []}>
      {children}
    </ResponsableSalesProvider>
  );
};

/** BPO rows show their project instead of a responsable. */
const ResponsableCell = ({ item }: { item: InventoryItem }) =>
  item.origin === "bpo" ? (
    <span>{item.project_name ?? "—"}</span>
  ) : (
    <ResponsableLabel option={item} />
  );

/**
 * Almikia Epic EC2d (TASK-137): "Inventario Almikia" board. It lists the read-only
 * `inventory_items` view (options, exclusivas and BPO units, so every asesora sees all of it),
 * newest first, with server-side pagination and filters. The view carries no owner contact and
 * no commission, and the listing date / days in inventory are never shown here. An option row
 * opens its detail; a BPO row opens its project when the viewer can, otherwise it is inert.
 */
export const OpcionList = () => {
  const translate = useTranslate();
  const { canAccess: canOpenProject } = useCanAccess({
    resource: "projects",
    action: "edit",
  });

  const rowTarget = (item: InventoryItem): string | false => {
    if (item.source === "option") {
      return `/property_options/${item.source_id}/show`;
    }
    return canOpenProject === true && item.project_id != null
      ? `/projects/${item.project_id}`
      : false;
  };

  return (
    <List
      resource="inventory_items"
      title={translate("resources.property_options.name", { smart_count: 2 })}
      sort={{ field: "added_on", order: "DESC" }}
      perPage={25}
      filterDefaultValues={INVENTORY_DEFAULT_FILTERS}
      exporter={false}
      actions={
        <CanAccess resource="property_options" action="create">
          <CreateButton resource="property_options" variant="gradient" />
        </CanAccess>
      }
    >
      <InventoryStatsCard />
      <InventoryFilters />
      <ResponsableBatch>
        <InventoryTable
          rowTarget={rowTarget}
          empty={
            <ResourceContextProvider value="property_options">
              <DataTableEmpty defaultFilterValues={INVENTORY_DEFAULT_FILTERS} />
            </ResourceContextProvider>
          }
        />
      </ResponsableBatch>
    </List>
  );
};

const InventoryTable = ({
  rowTarget,
  empty,
}: {
  rowTarget: (item: InventoryItem) => string | false;
  empty: ReactNode;
}) => {
  const translate = useTranslate();
  const { labels } = usePropertyTypes();
  return (
    <DataTable
      rowClick={(_id: Identifier, _resource: string, record: InventoryItem) =>
        rowTarget(record)
      }
      rowClassName={(record: InventoryItem) =>
        rowTarget(record) === false ? "cursor-default" : undefined
      }
      bulkActionButtons={false}
      empty={empty}
    >
      <DataTable.Col
        source="address"
        label="resources.property_options.fields.address"
        render={(record: InventoryItem) => (
          <div className="min-w-0">
            <div className="font-medium">{record.address}</div>
            {record.zone ? (
              <div className="text-sm text-muted-foreground">{record.zone}</div>
            ) : null}
          </div>
        )}
      />
      <DataTable.Col
        source="origin"
        label="resources.property_options.fields.origin"
        render={(record: InventoryItem) => (
          <OriginPill origin={record.origin} />
        )}
      />
      <DataTable.Col
        source="property_type"
        label="resources.property_options.fields.property_type"
        className="hidden md:table-cell"
        headerClassName="hidden md:table-cell"
        render={(record: InventoryItem) =>
          labels.get(record.property_type) ??
          translate(`crm.opciones.type.${record.property_type}`, {
            _: record.property_type,
          })
        }
      />
      <DataTable.Col
        source="operation"
        label="resources.property_options.fields.operation"
        className="hidden md:table-cell"
        headerClassName="hidden md:table-cell"
        render={(record: InventoryItem) => (
          <OperationLabel
            operation={record.operation}
            furnished={record.furnished}
          />
        )}
      />
      <DataTable.Col
        source="bedrooms"
        label="resources.property_options.fields.specs"
        disableSort
        className="hidden lg:table-cell"
        headerClassName="hidden lg:table-cell"
        render={(record: InventoryItem) => <KeySpecs item={record} />}
      />
      <DataTable.Col
        source="price"
        label="resources.property_options.fields.price"
        className="text-right tabular-nums"
        headerClassName="text-right"
        render={(record: InventoryItem) =>
          record.price == null ? "—" : formatMxn(record.price)
        }
      />
      <DataTable.Col
        source="status"
        label="resources.property_options.fields.status"
        render={(record: InventoryItem) => (
          <OpcionStatusPill status={record.status as PropertyOptionStatus} />
        )}
      />
      <DataTable.Col
        source="responsable_sales_id"
        label="resources.property_options.fields.responsable"
        disableSort
        render={(record: InventoryItem) => <ResponsableCell item={record} />}
      />
    </DataTable>
  );
};
